/**
 * Binom flow read/update hooks (Flow Optimizer Standard tab).
 *
 * Read: GET /binom/flows/{id} — server fetches the Binom rotation, maps it
 * to the unified Flow shape (paths / landers / offers).
 *
 * Update: PUT /binom/flows/{id} — server-side does the 2-request sequence
 * (GET rotation, merge weights + enabled, PUT rotation back) preserving every
 * other rotation field.
 *
 * For Media Devoted | Created By Ciro Autiero
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import api from '@/services/api';
import type { Flow } from '@/hooks/voluum/useVoluum';
import { binomKeys } from './useBinom';

interface PathDraft {
  id: string;
  name: string;
  weight: number;
  active: boolean;
  landers: Array<{ id: string; name: string; weight: number; active?: boolean }>;
  offers: Array<{ id: string; name: string; weight: number; active?: boolean }>;
}

interface PathGroupDraft {
  id: string;
  name: string;
  active: boolean;
  smartRotation: boolean;
  paths: PathDraft[];
}

export type BinomUpdateTarget = 'campaign' | 'rotation' | 'both';

export interface BinomCampaignMetaPatch {
  name?: string;
  groupUuid?: string;
  domainUuid?: string;
  trafficSourceId?: number;
  costModel?: 'CPC' | 'CPM' | 'CPA';
  currency?: string;
  amount?: number;
  isAuto?: boolean;
  distributionType?: string;
  hideReferrerType?: string;
  hideReferrerDomain?: string;
  tags?: Array<string | number>;
  vpnProxyDetection?: boolean;
  botDetection?: boolean;
  notes?: string;
  campaignSettings?: {
    payoutPercent?: number;
    postbackPercent?: number;
    trafficLossPercent?: number;
  };
}

export interface BinomFlowUpdateBody {
  id: string;
  name: string;
  isDefault: boolean;
  defaultPaths: PathDraft[];
  pathGroups?: PathGroupDraft[];
  /** Which Binom endpoint(s) to write. Default server-side: 'campaign' for inline, 'rotation' for shared. */
  target?: BinomUpdateTarget;
  /** Required for shared-rotation campaign / both saves so the server knows which campaign to update. */
  campaignId?: string;
  /** Partial campaign metadata (Main Options / Advanced fields). Only present when fields changed. */
  campaignMeta?: BinomCampaignMetaPatch;
}

export function useBinomFlow(flowId: string | undefined) {
  return useQuery({
    queryKey: ['binom', 'flow', flowId ?? ''] as const,
    queryFn: async () => {
      const response = await api.get<{ data: Flow }>(
        `/binom/flows/${encodeURIComponent(flowId!)}`,
      );
      return response.data?.data ?? null;
    },
    enabled: Boolean(flowId),
    staleTime: 30_000,
  });
}

export function useUpdateBinomFlow(flowId: string, campaignId?: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: BinomFlowUpdateBody) => {
      const response = await api.put<{ data: { success: boolean; message?: string } }>(
        `/binom/flows/${encodeURIComponent(flowId)}`,
        body,
      );
      return response.data?.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['binom', 'flow', flowId] });
      if (campaignId) {
        queryClient.invalidateQueries({
          queryKey: binomKeys.campaignFlows(campaignId),
        });
      }
    },
  });
}
